import { Button, Grid, Typography } from "@mui/material";
import { Field, Form, Formik } from "formik";
import { TextField } from "formik-mui";
import { useTranslation } from "react-i18next";

import { PointBasedGrid, APIError } from "src/types";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { ButtonLoading } from "src/components/ButtonLoading";
import { minMaxToString } from "src/utils/string.utils";
import { PointBasedGridMutationData } from "src/hooks/grids";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { adornments } from "src/config/adornments";
import { usePointBasedGridSchema } from "src/schemas/point-based-grid.schema";

interface Props {
  data: PointBasedGrid;
  isLoading: boolean;
  errors?: APIError[] | null;
  readOnly?: boolean;
  onClose: () => void;
  onSubmit: (values: PointBasedGridMutationData) => void;
}

export function PointBasedGridForm(props: Props) {
  const { t } = useTranslation();
  const validationSchema = usePointBasedGridSchema(props.data);

  const initialValues: PointBasedGridMutationData = {
    numberOfCircles: props.data.numberOfCircles.value,
    pointsPerCircle: props.data.pointsPerCircle.value,
    minDistance: props.data.minDistance.value,
    maxDistance: props.data.maxDistance.value,
    tolerance: props.data.tolerance.value,
  };

  return render();

  function render() {
    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values: PointBasedGridMutationData) => {
          return props.onSubmit(values);
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography>{t("label.pointBasedGrid")}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="numberOfCircles"
                  label={t("label.numberOfCircles")}
                  helperText={minMaxToString(props.data.numberOfCircles.minMaxDefault)}
                  type="number"
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="pointsPerCircle"
                  label={t("label.pointsPerCircle")}
                  helperText={minMaxToString(props.data.pointsPerCircle.minMaxDefault)}
                  type="number"
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="minDistance"
                  label={t("label.distanceOfFirstCircle")}
                  helperText={minMaxToString(props.data.minDistance.minMaxDefault)}
                  InputProps={adornments(t).meter}
                  type="number"
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="maxDistance"
                  label={t("label.distanceOfLastCircle")}
                  helperText={minMaxToString(props.data.maxDistance.minMaxDefault)}
                  InputProps={adornments(t).meter}
                  type="number"
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="tolerance"
                  label={t("label.tolerance")}
                  helperText={minMaxToString(props.data.tolerance.minMaxDefault)}
                  InputProps={adornments(t).meter}
                  type="number"
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                />
              </Grid>

              {props.errors && (
                <Grid item xs={12}>
                  <ServerErrors data={props.errors} />
                </Grid>
              )}

              {!props.readOnly && (
                <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                  <Button variant="text" onClick={props.onClose}>
                    {t("label.cancel")}
                  </Button>
                  <ButtonLoading
                    isLoading={isSubmitting || props.isLoading}
                    type="submit"
                    color="primary"
                    variant="contained"
                  >
                    {t("label.save")}
                  </ButtonLoading>
                </ButtonGrid>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    );
  }
}
